import { z } from 'zod'

import { pruned } from '@/lib/filters'

/** The status groups the order book counts, as tabs: every marketplace's statuses in six. */
export const ORDER_STATUSES = [
  'pending',
  'shipped',
  'delivered',
  'cancelled',
  'returned',
  'other',
] as const
export type OrderStatus = (typeof ORDER_STATUSES)[number]

export const ORDER_PAGE_SIZES = [25, 50, 100] as const
export const DEFAULT_ORDER_PAGE_SIZE = 50

const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
const ids = z.array(z.number().int().positive()).max(50).optional().catch(undefined)

/**
 * Live Seller Orders' URL: the reports' scope (warehouses, the marketplace,
 * its stores, brands) and the range — of the purchase or of the last update;
 * fulfilment, order types and the ship-to country; the status tab; the
 * order-id and product searches, the sort
 * and the page; whether the analytics count orders or units; whether they
 * are folded; and the order opened in the table. Every field optional;
 * defaults apply at read time.
 */
export const sellerOrdersSearchSchema = z.object({
  warehouseIds: ids.describe('Warehouses to read, by id; empty means every warehouse.'),
  marketplaceId: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The marketplace to read, by id; absent means every marketplace.'),
  storeIds: ids.describe('Stores to include, by id; empty means every store.'),
  brandIds: ids.describe('Brands to include, by id; empty means every brand.'),
  dateFrom: day
    .optional()
    .catch(undefined)
    .describe('First day of the range, YYYY-MM-DD; the last 30 days when absent.'),
  dateTo: day
    .optional()
    .catch(undefined)
    .describe('Last day of the range, YYYY-MM-DD, inclusive; today when absent.'),
  dated: z
    .enum(['updated'])
    .optional()
    .catch(undefined)
    .describe('updated applies the date range to the last update; absent, to the purchase date.'),
  fulfilment: z
    .enum(['fba', 'fbm'])
    .optional()
    .catch(undefined)
    .describe('fba: the marketplace ships; fbm: the seller ships. Absent is any fulfilment.'),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional()
    .catch(undefined)
    .describe('Ship-to country, ISO 3166 alpha-2.'),
  status: z
    .enum(ORDER_STATUSES)
    .optional()
    .catch(undefined)
    .describe('Order status tab; narrows the table only, not the analytics. Absent is All.'),
  // VentaHub's two searches: the order id, and a product (ASIN, seller SKU, SKU).
  orderRef: z.string().max(64).optional().catch(undefined).describe('Order ID search.'),
  product: z
    .string()
    .max(64)
    .optional()
    .catch(undefined)
    .describe('ASIN, seller SKU or SKU search.'),
  // The marketplace's own order types, lower case.
  types: z
    .array(
      z
        .string()
        .regex(/^[\p{L}\p{N} _-]+$/u)
        .max(40),
    )
    .max(20)
    .optional()
    .catch(undefined)
    .describe('The marketplace’s own order types to include, lower case (e.g. standardorder).'),
  sort: z
    .string()
    .regex(/^[a-z_]+$/)
    .max(64)
    .optional()
    .catch(undefined)
    .describe('The column the orders are sorted by on the server.'),
  order: z
    .enum(['asc', 'desc'])
    .optional()
    .catch(undefined)
    .describe('Sort direction; desc when a sort is set.'),
  page: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('Page of the table, from 1; default 1.'),
  limit: z
    .union([z.literal(25), z.literal(50), z.literal(100)])
    .optional()
    .catch(undefined)
    .describe('Rows per page; default 50.'),
  // The analytics by units rather than orders.
  units: z
    .literal(1)
    .optional()
    .catch(undefined)
    .describe('1 makes the analytics count units rather than orders.'),
  // The analytics folded away: absent is open.
  charts: z
    .enum(['hidden'])
    .optional()
    .catch(undefined)
    .describe('hidden folds the Order analytics away; absent is open.'),
  // The order opened in the table, so an open order can be shared.
  open: z
    .number()
    .int()
    .positive()
    .optional()
    .catch(undefined)
    .describe('The order expanded in the table, by its id.'),
})

export type SellerOrdersSearch = z.infer<typeof sellerOrdersSearchSchema>

export const EMPTY_SELLER_ORDERS_SEARCH: SellerOrdersSearch = sellerOrdersSearchSchema.parse({})

/** Reset: every field cleared, so the page is as it first opened. */
export const RESET_SELLER_ORDERS: Partial<SellerOrdersSearch> = Object.fromEntries(
  Object.keys(sellerOrdersSearchSchema.shape).map((key) => [key, undefined]),
)

/** Strip defaults and empties so a clean URL stays clean. */
export function withoutSellerOrdersDefaults(search: SellerOrdersSearch): SellerOrdersSearch {
  return pruned({
    ...search,
    limit: search.limit === DEFAULT_ORDER_PAGE_SIZE ? undefined : search.limit,
  })
}
